import cms from 'shared/services/cms';
import { v4 as uuidv4 } from 'uuid';
import { ICheckouRequest } from '../models/Payment-subscription.interface';

interface ICreateOrUpdateOrder {
  email: string;
  phone: string;
  country: number;
  plans: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  raw_data: any;
  orderId?: string;
}

interface IOrderData extends ICreateOrUpdateOrder {
  id: string;
  created_at: string;
  updated_at?: string;
}

export async function makePayment(
  subscription: ICheckouRequest,
  tokenCaptcha: string
) {
  const data = {
    ...subscription,
    authorizationToken: tokenCaptcha,
  };

  const response = await cms.post('user-subscriptions/direct-selling', data);
  return response.data;
}

export const createOrderId = async (data: ICreateOrUpdateOrder): Promise<string> => {
  const orderId = uuidv4();
  
  const orderData: IOrderData = {
    id: orderId,
    ...data,
    created_at: new Date().toISOString(),
  };
  
  try {
    localStorage.setItem(`order_${orderId}`, JSON.stringify(orderData));
    cleanupOldOrders();
  } catch (error) {
    console.warn('Could not store order data in localStorage:', error);
  }
  
  return orderId;
};

export const updateOrderId = async (data: ICreateOrUpdateOrder): Promise<IOrderData> => {
  if (!data.orderId) {
    throw new Error('Order ID is required for update');
  }
  
  let orderData: IOrderData = {} as IOrderData;
  try {
    const storedData = localStorage.getItem(`order_${data.orderId}`);
    if (storedData) {
      orderData = JSON.parse(storedData);
    }
  } catch (error) {
    console.warn('Could not retrieve order data from localStorage:', error);
  }
  
  const updatedOrderData: IOrderData = {
    ...orderData,
    ...data,
    updated_at: new Date().toISOString(),
  };
  
  try {
    localStorage.setItem(`order_${data.orderId}`, JSON.stringify(updatedOrderData));
  } catch (error) {
    console.warn('Could not update order data in localStorage:', error);
  }
  
  return updatedOrderData;
};

export const getOrderData = (orderId: string): IOrderData | null => {
  try {
    const storedData = localStorage.getItem(`order_${orderId}`);
    if (storedData) {
      return JSON.parse(storedData);
    }
  } catch (error) {
    console.warn('Could not retrieve order data from localStorage:', error);
  }
  return null;
};

const cleanupOldOrders = (): void => {
  try {
    const oneDayAgo = new Date();
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('order_')) {
        try {
          const orderData = JSON.parse(localStorage.getItem(key) || '{}');
          const orderDate = new Date(orderData.created_at);
          
          if (orderDate < oneDayAgo) {
            keysToRemove.push(key);
          }
        } catch (error) {
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    if (keysToRemove.length > 0) {
      console.log(`Cleaned up ${keysToRemove.length} old orders from localStorage`);
    }
  } catch (error) {
    console.warn('Could not cleanup old orders:', error);
  }
};
